import React, { useState, useCallback, useEffect, useRef } from 'react';

import Button from '~c/components/base/button';
import { MicrosoftIcon } from '~c/components/base/icon/microsoft';
import { spc } from '~c/components/base/spacer';
import {
  msalConfig,
  loginRequest,
  clearStaleInteractionState,
} from '~c/config/msal-config';
import { nodeAuthApi } from '~c/services';

type PublicClientApplicationType =
  typeof import('@azure/msal-browser').PublicClientApplication;

interface MicrosoftLoginButtonProps {
  onError?: (error: string) => void;
  on2FARequired?: (idToken: string) => void;
  loading?: boolean;
  invitationToken?: string;
  /**
   * Azure tenant ID resolved from the user's email domain via SSO discovery.
   * Takes precedence over the tenant returned by the global /microsoft/config
   * endpoint, which only looks up the first org that has a tenant configured.
   */
  tenantId?: string;
}

/** Map MSAL error codes to user-friendly messages. */
function extractMsalErrorInfo(error: any): {
  userMessage: string;
  isUserCancelled: boolean;
} {
  const errorCode: string = error.errorCode ?? '';
  const errorMessage: string = error.errorMessage ?? error.message ?? '';
  const subError: string = error.subError ?? '';
  const correlationId: string = error.correlationId ?? '';

  // eslint-disable-next-line no-console
  console.error('[MSAL] Login error details:', {
    errorCode,
    errorMessage,
    subError,
    correlationId,
    name: error.name,
    stack: error.stack?.substring(0, 500),
  });

  // User cancelled
  if (
    errorCode === 'user_cancelled' ||
    errorCode === 'user_login_error' ||
    subError === 'cancel'
  ) {
    return { userMessage: '', isUserCancelled: true };
  }

  // Popup blocked
  if (
    errorCode === 'popup_window_error' ||
    errorCode === 'empty_window_error'
  ) {
    return {
      userMessage:
        'Popup was blocked by the browser. Please allow popups for this site and try again.',
      isUserCancelled: false,
    };
  }

  if (errorCode === 'interaction_in_progress') {
    return {
      userMessage:
        'A sign-in attempt is already in progress. Please wait a moment and try again.',
      isUserCancelled: false,
    };
  }

  // Timeout
  if (
    errorCode === 'timed_out' &&
    (subError === 'redirect_bridge_timeout' || !subError)
  ) {
    return {
      userMessage:
        'Sign-in timed out. Please try again. If the popup did not appear, allow popups for this site.',
      isUserCancelled: false,
    };
  }

  // Empty response
  if (
    errorCode === 'redirect_bridge_empty_response' ||
    errorCode === 'empty_response'
  ) {
    return {
      userMessage: 'No response was received from Microsoft. Please try again.',
      isUserCancelled: false,
    };
  }

  // State mismatch
  if (errorCode === 'state_mismatch' || errorCode === 'state_not_found') {
    return {
      userMessage:
        'Authentication state mismatch. Please clear your browser cache and try again.',
      isUserCancelled: false,
    };
  }

  // Token exchange failure
  if (
    errorCode === 'token_renewal_error' ||
    errorCode === 'endpoints_resolution_error' ||
    errorCode === 'invalid_grant'
  ) {
    return {
      userMessage:
        'Failed to complete sign-in with Microsoft. Please try again.',
      isUserCancelled: false,
    };
  }

  // Response parsing errors
  if (
    errorCode === 'hash_empty_error' ||
    errorCode === 'hash_does_not_contain_known_properties' ||
    errorCode === 'hash_not_deserialized' ||
    errorCode === 'unable_to_parse_state'
  ) {
    return {
      userMessage:
        'Failed to process the authentication response. Please try again.',
      isUserCancelled: false,
    };
  }

  // Azure AD configuration errors
  if (
    errorCode === 'invalid_client' ||
    errorMessage.includes('AADSTS7000218') ||
    errorMessage.includes('AADSTS700054')
  ) {
    // eslint-disable-next-line no-console
    console.error(
      '[MSAL] Azure AD app registration issue. Ensure redirect URI is under SPA platform, not Web.'
    );
    return {
      userMessage:
        'Microsoft authentication configuration error. Please contact support.',
      isUserCancelled: false,
    };
  }

  const fallbackMessage =
    errorMessage || 'Microsoft sign-in failed. Please try again.';

  return { userMessage: fallbackMessage, isUserCancelled: false };
}

/** MSAL v5 popup login button. Sends idToken to backend for session creation. */
export const MicrosoftLoginButton: React.FC<MicrosoftLoginButtonProps> = ({
  onError,
  on2FARequired,
  loading: externalLoading = false,
  invitationToken,
  tenantId,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [msalError, setMsalError] = useState<string | null>(null);
  const [fetchMicrosoftConfig] = nodeAuthApi.useLazyMicrosoftConfigQuery();
  const [microsoftLogin] = nodeAuthApi.useMicrosoftLoginMutation();
  const [microsoftLoginWithInvitation] =
    nodeAuthApi.useMicrosoftLoginWithInvitationMutation();

  const msalClassRef = useRef<PublicClientApplicationType | null>(null);
  const [msalLoaded, setMsalLoaded] = useState(false);

  // Lazy-load MSAL (browser-only, code-split)
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let cancelled = false;

    const loadMsal = async () => {
      try {
        const msalBrowser = await import(
          /* webpackChunkName: "msal-browser" */
          '@azure/msal-browser'
        );

        if (cancelled) {
          return;
        }

        if (!msalBrowser.PublicClientApplication) {
          throw new Error(
            'PublicClientApplication not found in MSAL browser bundle'
          );
        }

        msalClassRef.current = msalBrowser.PublicClientApplication;
        setMsalLoaded(true);
      } catch (err) {
        if (cancelled) {
          return;
        }

        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('[MSAL] Failed to load:', err);
        }
        setMsalError(
          'Failed to load Microsoft authentication. Please refresh and try again.'
        );
      }
    };

    loadMsal();

    return () => {
      cancelled = true;
    };
  }, []);

  const isMsalLoading =
    typeof window !== 'undefined' && !msalLoaded && !msalError;
  const isLoading = externalLoading || isProcessing || isMsalLoading;

  const handleMicrosoftLogin = useCallback(async () => {
    if (typeof window === 'undefined') {
      return;
    }

    if (msalError) {
      onError?.(msalError);
      return;
    }

    if (!msalClassRef.current) {
      onError?.('Microsoft authentication is still loading. Please try again.');
      return;
    }

    if (isProcessing) {
      return;
    }

    setIsProcessing(true);

    let settled = false;
    let userClosedPopup = false;
    let navigating = false;

    try {
      const configResult = await fetchMicrosoftConfig().unwrap();
      const effectiveTenantId = tenantId ?? configResult.tenantId;
      if (!configResult.clientId || !effectiveTenantId) {
        throw new Error('Microsoft auth not configured');
      }

      const msalConfiguration = msalConfig(
        effectiveTenantId,
        configResult.clientId
      );

      clearStaleInteractionState(configResult.clientId);

      const PublicClientApplication = msalClassRef.current;
      const instance = new PublicClientApplication(msalConfiguration);
      await instance.initialize();

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.info(
          '[MSAL] PCA initialized. Starting loginPopup() with redirectUri:',
          msalConfiguration.auth.redirectUri
        );
      }

      // Capture popup ref to detect manual close (MSAL v5 only uses
      // BroadcastChannel, so manual close causes a 60s timeout without this)
      let popupWindow: Window | null = null;
      const originalWindowOpen = window.open;
      window.open = function (...args: Parameters<typeof window.open>) {
        const popup = originalWindowOpen.apply(this, args);
        popupWindow = popup;
        return popup;
      };

      let popupCloseInterval: ReturnType<typeof setInterval> | null = null;
      let response;

      try {
        const loginPromise = instance.loginPopup({
          ...loginRequest,
          prompt: 'select_account',
        });

        // Poll popup.closed to detect manual close with a grace period
        // so we don't false-positive during MSAL's token exchange
        let popupClosedAt: number | null = null;
        const POPUP_CLOSE_THRESHOLD_MS = 1500;

        popupCloseInterval = setInterval(() => {
          if (settled) {
            if (popupCloseInterval) {
              clearInterval(popupCloseInterval);
              popupCloseInterval = null;
            }
            return;
          }

          if (popupWindow?.closed) {
            if (popupClosedAt === null) {
              popupClosedAt = Date.now();
              return;
            }

            if (Date.now() - popupClosedAt >= POPUP_CLOSE_THRESHOLD_MS) {
              if (popupCloseInterval) {
                clearInterval(popupCloseInterval);
                popupCloseInterval = null;
              }
              userClosedPopup = true;
              setIsProcessing(false);
            }
          }
        }, 500);

        response = await loginPromise;
        settled = true;
      } finally {
        window.open = originalWindowOpen;
        if (popupCloseInterval) {
          clearInterval(popupCloseInterval);
          popupCloseInterval = null;
        }
      }

      // Re-assert after popup monitor may have reset it
      setIsProcessing(true);

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.info(
          '[MSAL] loginPopup() succeeded. Account:',
          response.account?.username,
          'Has idToken:',
          !!response.idToken
        );
      }

      if (!response.idToken) {
        throw new Error('No ID token received from Microsoft');
      }

      const result = invitationToken
        ? await microsoftLoginWithInvitation({
            idToken: response.idToken,
            invitationToken,
          })
        : await microsoftLogin({
            idToken: response.idToken,
          });

      if ('error' in result) {
        const errorResponse = result.error as {
          data?: { statusCode?: number; message?: string; error?: string };
          status?: number;
        };

        // eslint-disable-next-line no-console
        console.error(
          '[MSAL] Backend /microsoft/token returned error:',
          errorResponse
        );

        const status = errorResponse.status;
        let errorMessage: string;

        if (!status) {
          errorMessage =
            'Unable to reach the server. Please check your internet connection and try again.';
        } else if (status === 401) {
          errorMessage =
            errorResponse.data?.message ??
            'Microsoft authentication failed. Please try again.';
        } else if (status === 400) {
          errorMessage =
            errorResponse.data?.message ??
            'Invalid authentication request. Please try again.';
        } else if (status === 429) {
          errorMessage =
            'Too many login attempts. Please wait a moment and try again.';
        } else if (status >= 500) {
          errorMessage =
            'Microsoft authentication failed due to a server error. Please try again later.';
        } else {
          errorMessage =
            errorResponse.data?.message ??
            'Microsoft authentication failed. Please try again.';
        }

        onError?.(errorMessage);
        return;
      }

      const data = result.data;

      // Check 2FA before success flag (2FA returns success: false)
      if (
        data?.requires2FA ||
        (data?.success === false && data?.user?.otpEnabled)
      ) {
        if (on2FARequired) {
          on2FARequired(response.idToken);
        } else {
          onError?.('Two-factor authentication is required.');
        }
        return;
      }

      if (!data || data.success === false) {
        const errorMessage = data?.message ?? 'Account verification required';
        onError?.(errorMessage);
        return;
      }

      // Full page nav to refresh CSRF token invalidated by cross-site popup redirect
      navigating = true;

      if (invitationToken) {
        window.location.href = `/accept-invitation?token=${encodeURIComponent(invitationToken)}`;
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const invitation = params.get('invitation');

      if (invitation) {
        window.location.href = `/accept-invitation?token=${invitation}`;
      } else {
        window.location.href = '/select-organization';
      }
    } catch (error: any) {
      settled = true;

      const { userMessage, isUserCancelled } = extractMsalErrorInfo(error);

      if (isUserCancelled) {
        return;
      }

      // Suppress delayed bridge timeout after user manually closed popup
      if (userClosedPopup) {
        return;
      }

      onError?.(userMessage);
    } finally {
      if (!navigating) {
        setIsProcessing(false);
      }
    }
  }, [
    isProcessing,
    msalError,
    onError,
    on2FARequired,
    fetchMicrosoftConfig,
    microsoftLogin,
    microsoftLoginWithInvitation,
    invitationToken,
    tenantId,
  ]);

  if (typeof window === 'undefined') {
    return null;
  }

  return (
    <Button
      onClick={handleMicrosoftLogin}
      loading={isLoading}
      fullWidth
      variant="outlined"
      sx={{ mt: spc.xs, mb: spc.between_vertical_s }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <MicrosoftIcon />
        <div>Continue with Microsoft</div>
      </div>
    </Button>
  );
};

export default MicrosoftLoginButton;
